import React, { useEffect, useState } from 'react'
import { Card, Text } from '../ui'

/**
 * Props interface for navigation section data
 */
export interface NavigationSection {
  id: string
  title: string
  href: string
}

/**
 * Props interface for the CaseStudyNavigation component
 */
export interface CaseStudyNavigationProps {
  /** Array of sections to navigate between */
  sections: NavigationSection[]
  /** Additional CSS classes */
  className?: string
}

/**
 * Case Study Navigation component for section-based navigation
 *
 * Features:
 * - Smooth scrolling to sections
 * - Active section highlighting
 * - Sticky navigation behavior
 * - Keyboard accessible
 * - Mobile-responsive design
 * - Progress indication
 */
export const CaseStudyNavigation: React.FC<CaseStudyNavigationProps> = ({
  sections,
  className = '',
}) => {
  const [activeSection, setActiveSection] = useState<string>('')

  // Handle scroll to section
  const handleSectionClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    event.preventDefault()

    const targetElement = document.querySelector(href)
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      // Update URL without triggering navigation
      window.history.replaceState(null, '', href)
    }
  }

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100 // Offset for header

      for (const section of sections) {
        const element = document.querySelector(section.href)
        if (element) {
          const { offsetTop, offsetHeight } = element as HTMLElement

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section.id)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check initial position

    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  if (sections.length === 0) {
    return null
  }

  return (
    <nav
      className={`case-study-navigation ${className}`}
      role="navigation"
      aria-label="Case study sections"
    >
      <Card variant="glass" padding="md" className="navigation-card">
        <Text size="sm" variant="secondary" className="navigation-title">
          Contents
        </Text>

        <ul className="navigation-list" role="menubar">
          {sections.map((section, index) => (
            <li key={section.id} className="navigation-item" role="none">
              <a
                href={section.href}
                className={`navigation-link ${
                  activeSection === section.id ? 'active' : ''
                }`}
                role="menuitem"
                aria-current={activeSection === section.id ? 'page' : undefined}
                onClick={e => handleSectionClick(e, section.href)}
                tabIndex={0}
              >
                <span className="link-number">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="link-text">{section.title}</span>
              </a>
            </li>
          ))}
        </ul>

        {/* Progress Indicator */}
        <div
          className="navigation-progress"
          role="progressbar"
          aria-label="Reading progress"
        >
          <div
            className="progress-bar"
            style={{
              transform: `scaleX(${sections.findIndex(s => s.id === activeSection) / (sections.length - 1) || 0})`,
            }}
          />
        </div>
      </Card>
    </nav>
  )
}

export default CaseStudyNavigation
